import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PrismMark } from "@/components/auth/PrismMark";
import { OtpInput } from "@/components/auth/OtpInput";
import {
  getMe,
  login as apiLogin,
  verifyLogin2FA,
} from "@/api/auth";
import { useAuthStore } from "@/stores/auth.store";
import { cn } from "@/lib/utils";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

type LoginValues = z.infer<typeof loginSchema>;

type Step = "credentials" | "totp";
type CodeMode = "totp" | "backup";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("credentials");
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [codeMode, setCodeMode] = useState<CodeMode>("totp");
  const [code, setCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const otpRef = useRef<HTMLInputElement>(null);

  const from = (location.state as { from?: string } | null)?.from ?? "/projects";

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, navigate, from]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(values: LoginValues) {
    setSubmitError(null);
    try {
      const res = await apiLogin(values);
      if (res.requires_2fa && res.temp_token) {
        setTempToken(res.temp_token);
        setStep("totp");
        setCodeMode("totp");
        setCode("");
        setBackupCode("");
        return;
      }
      if (!res.access_token || !res.refresh_token) {
        throw new Error("Unexpected response from server.");
      }
      setTokens({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
        token_type: res.token_type,
      });
      const me = await getMe();
      setUser(me);
      toast.success(`Welcome back, ${me.full_name}`);
      navigate(from, { replace: true });
    } catch (err) {
      const message = extractErrorMessage(err);
      setSubmitError(message);
      toast.error(message);
    }
  }

  function backToCredentials() {
    setStep("credentials");
    setTempToken(null);
    setCode("");
    setBackupCode("");
    setCodeMode("totp");
    setSubmitError(null);
  }

  async function verify2FA(submitted: string) {
    if (!tempToken) {
      toast.error("Session expired. Please sign in again.");
      backToCredentials();
      return;
    }
    setVerifying(true);
    try {
      const tokens = await verifyLogin2FA(tempToken, submitted);
      setTokens(tokens);
      const me = await getMe();
      setUser(me);
      toast.success(`Welcome back, ${me.full_name}`);
      navigate(from, { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        // 401 with a temp_token error usually means the temp token expired.
        const detail = (err.response?.data as { detail?: string } | undefined)
          ?.detail;
        if (
          typeof detail === "string" &&
          /expir|invalid token/i.test(detail)
        ) {
          toast.error("Session expired. Please sign in again.");
          backToCredentials();
          return;
        }
      }
      toast.error("Invalid code");
      setCode("");
      setBackupCode("");
      setTimeout(() => otpRef.current?.focus(), 0);
    } finally {
      setVerifying(false);
    }
  }

  function onTotpSubmit(e: React.FormEvent) {
    e.preventDefault();
    const submitted = codeMode === "totp" ? code : backupCode.trim();
    if (codeMode === "totp" && submitted.length !== 6) return;
    if (codeMode === "backup" && submitted.length === 0) return;
    void verify2FA(submitted);
  }

  return (
    <div className="flex min-h-screen bg-white">
      <div
        className="relative hidden flex-col items-center justify-between overflow-hidden p-10 md:flex md:w-1/2"
        style={{
          background:
            "linear-gradient(135deg, #0f0f14 0%, #1a1a2e 60%, #1f1d40 100%)",
        }}
      >
        <div className="pointer-events-none absolute inset-0 opacity-50">
          <div className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-indigo-600/30 mix-blend-screen blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-violet-500/20 mix-blend-screen blur-[100px]" />
          <div className="absolute left-1/2 top-1/2 h-64 w-64 rounded-full bg-cyan-300/10 mix-blend-screen blur-[80px]" />
        </div>

        <div className="z-10 flex flex-1 flex-col items-center justify-center text-center">
          <PrismMark size={128} className="mb-8 text-white/80" />
          <h1 className="mb-3 text-[28px] font-light uppercase tracking-[0.25em] text-white">
            Prism
          </h1>
          <p className="text-sm font-light tracking-wide text-white/40">
            See what others don&rsquo;t.
          </p>
        </div>

        <div className="z-10 text-center">
          <p className="text-[11px] font-light uppercase tracking-wider text-white/20">
            B2B Lead Engine · Enterprise
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-slate-50 p-8 md:w-1/2">
        <div className="w-full max-w-[380px]">
          {step === "credentials" ? (
            <CredentialsStep
              register={register}
              errors={errors}
              submitError={submitError}
              isSubmitting={isSubmitting}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              onSubmit={handleSubmit(onSubmit)}
            />
          ) : (
            <TwoFactorStep
              codeMode={codeMode}
              setCodeMode={(m) => {
                setCodeMode(m);
                setCode("");
                setBackupCode("");
              }}
              code={code}
              setCode={setCode}
              backupCode={backupCode}
              setBackupCode={setBackupCode}
              verifying={verifying}
              onSubmit={onTotpSubmit}
              onBack={backToCredentials}
              otpRef={otpRef}
              onAutoSubmit={(v) => verify2FA(v)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface CredentialsStepProps {
  register: ReturnType<typeof useForm<LoginValues>>["register"];
  errors: ReturnType<typeof useForm<LoginValues>>["formState"]["errors"];
  submitError: string | null;
  isSubmitting: boolean;
  showPassword: boolean;
  setShowPassword: (fn: (v: boolean) => boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
}

function CredentialsStep({
  register,
  errors,
  submitError,
  isSubmitting,
  showPassword,
  setShowPassword,
  onSubmit,
}: CredentialsStepProps) {
  return (
    <>
      <div className="mb-10">
        <h2 className="mb-2 text-3xl font-semibold tracking-tight text-slate-900">
          Welcome back
        </h2>
        <p className="text-[15px] font-light text-slate-500">
          Sign in to continue
        </p>
      </div>

      <form className="space-y-5" onSubmit={onSubmit} noValidate>
        <div className="space-y-2">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-slate-700"
          >
            Email
          </label>
          <Input
            id="email"
            type="email"
            placeholder="name@company.com"
            autoComplete="email"
            aria-invalid={!!errors.email}
            className={cn(
              "h-11 rounded-xl border-slate-200 bg-white",
              errors.email && "border-rose-400 focus-visible:ring-rose-300",
            )}
            {...register("email")}
          />
          {errors.email && (
            <p className="text-xs text-rose-600">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="password"
            className="block text-sm font-medium text-slate-700"
          >
            Password
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              autoComplete="current-password"
              aria-invalid={!!errors.password}
              className={cn(
                "h-11 rounded-xl border-slate-200 bg-white pr-11",
                errors.password &&
                  "border-rose-400 focus-visible:ring-rose-300",
              )}
              {...register("password")}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 transition-colors hover:text-slate-700"
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-rose-600">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center justify-between pt-1 text-sm">
          <label className="flex items-center gap-2 font-light text-slate-500">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            Remember me
          </label>
          <a
            href="#"
            className="font-medium text-indigo-600 transition-colors hover:text-indigo-500"
            onClick={(e) => {
              e.preventDefault();
              toast.info("Contact your admin to reset your password.");
            }}
          >
            Forgot password?
          </a>
        </div>

        {submitError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {submitError}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
          className="h-11 w-full rounded-xl text-sm font-medium transition-all hover:opacity-95 disabled:opacity-70"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in…
            </>
          ) : (
            "Sign in"
          )}
        </Button>
      </form>
    </>
  );
}

interface TwoFactorStepProps {
  codeMode: CodeMode;
  setCodeMode: (m: CodeMode) => void;
  code: string;
  setCode: (v: string) => void;
  backupCode: string;
  setBackupCode: (v: string) => void;
  verifying: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  otpRef: React.RefObject<HTMLInputElement>;
  onAutoSubmit: (code: string) => void;
}

function TwoFactorStep({
  codeMode,
  setCodeMode,
  code,
  setCode,
  backupCode,
  setBackupCode,
  verifying,
  onSubmit,
  onBack,
  otpRef,
  onAutoSubmit,
}: TwoFactorStepProps) {
  const canSubmit =
    codeMode === "totp" ? code.length === 6 : backupCode.trim().length > 0;

  return (
    <div>
      <div className="mb-8 flex flex-col items-center text-center">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
          <ShieldCheck className="h-7 w-7" />
        </div>
        <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">
          Two-factor authentication
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          {codeMode === "totp"
            ? "Enter the 6-digit code from your authenticator app"
            : "Enter one of your backup codes"}
        </p>
      </div>

      <form className="space-y-5" onSubmit={onSubmit} noValidate>
        {codeMode === "totp" ? (
          <OtpInput
            ref={otpRef}
            value={code}
            onChange={setCode}
            autoFocus
            disabled={verifying}
            onComplete={(v) => onAutoSubmit(v)}
            ariaLabel="6-digit verification code"
          />
        ) : (
          <Input
            autoFocus
            placeholder="xxxx-xxxx"
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value)}
            disabled={verifying}
            autoComplete="one-time-code"
            className="h-12 rounded-xl border-slate-200 bg-white text-center font-mono text-base tracking-widest"
          />
        )}

        <Button
          type="submit"
          variant="primary"
          disabled={!canSubmit || verifying}
          className="h-11 w-full rounded-xl text-sm font-medium"
        >
          {verifying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying…
            </>
          ) : (
            "Verify"
          )}
        </Button>

        <div className="flex flex-col items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() =>
              setCodeMode(codeMode === "totp" ? "backup" : "totp")
            }
            disabled={verifying}
            className="text-sm font-medium text-indigo-600 transition-colors hover:text-indigo-500"
          >
            {codeMode === "totp"
              ? "Use a backup code instead"
              : "Use authenticator app"}
          </button>
          <button
            type="button"
            onClick={onBack}
            disabled={verifying}
            className="inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-slate-700"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to login
          </button>
        </div>
      </form>
    </div>
  );
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 401) {
      return "Invalid email or password.";
    }
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
    if (err.code === "ERR_NETWORK") {
      return "Cannot reach the server. Check your connection.";
    }
  }
  return "Something went wrong. Please try again.";
}
